import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock } from "lucide-react";

const LAST_UPDATED = "26 June 2026";

function Section({ n, title, children }) {
  return (
    <section className="mt-10 scroll-mt-24" id={`s-${n}`} data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-2xl tracking-tight">
        <span className="text-neutral-400 mr-2">{n}.</span>{title}
      </h2>
      <div className="mt-3 space-y-3 text-sm text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

const SECTIONS = [
  "Credit card & payment security", "Who we are & acceptance", "Definitions", "Eligibility & your account",
  "Our role in transactions", "Listing rules & quality standards", "Authenticity & anti-counterfeit policy",
  "Prohibited, restricted & dangerous goods", "Buying, offers & orders", "Fees, commission & subscriptions",
  "Payments & processing", "Shipping, pickup & delivery", "Buyer protection & authenticity guarantee",
  "Returns, claims & disputes", "Seller payouts & holds", "Prohibited conduct", "Your content & our licence",
  "Privacy & data", "Disclaimers", "Limitation of liability", "Indemnification", "Suspension & termination",
  "Governing law & dispute resolution", "Changes to these terms", "Contact us",
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-8">
        <div className="label-eyebrow">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Terms &amp; Conditions.</h1>
        <p className="mt-4 text-sm text-neutral-500 max-w-2xl">Last updated {LAST_UPDATED}</p>
        <p className="mt-4 text-base text-neutral-600 max-w-3xl leading-relaxed">
          These Terms govern your use of Your Classic Style — a peer-to-peer marketplace where individuals buy and
          sell pre-owned and vintage apparel, footwear and accessories. Please read them carefully. By creating an
          account, listing an item, or making a purchase, you agree to be bound by these Terms.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="h-fit lg:sticky lg:top-24 hairline p-5" data-testid="terms-toc">
          <div className="label-eyebrow">Contents</div>
          <ol className="mt-3 space-y-1.5 text-xs text-neutral-600">
            {SECTIONS.map((t, i) => (
              <li key={t}>
                <a href={`#s-${i + 1}`} className="hover:text-black transition-colors">{i + 1}. {t}</a>
              </li>
            ))}
          </ol>
        </aside>

        <div>
          <div className="hairline p-5 flex items-start gap-3" style={{ borderColor: "#2563EB" }} data-testid="terms-disclaimer">
            <ShieldCheck size={16} style={{ color: "#2563EB" }} className="mt-0.5 shrink-0" />
            <p className="text-xs text-neutral-600 leading-relaxed">
              This document is a plain-English template provided for your convenience and does not constitute legal
              advice. Before you operate commercially, please have a qualified lawyer in your jurisdiction review and
              adapt these Terms (and a separate Privacy Policy) to your business and applicable consumer-protection law.
            </p>
          </div>

          <Section n={1} title="Credit card & payment security">
            <div className="hairline p-4 flex items-start gap-3" style={{ borderColor: "#1E7A3B", background: "rgba(30,122,59,0.04)" }} data-testid="terms-payment-security">
              <Lock size={16} style={{ color: "#1E7A3B" }} className="mt-0.5 shrink-0" />
              <p className="leading-relaxed">
                For your piece of mind. Stripe is the Credit Card company we use to process your payments. We value your
                trust, and want to assure you, that we never see, keep, nor store your Credit Card information on our
                site. Stripe is one of the largest Credit Card processing companies in the world. With 22.3 % of all
                sales. A company valued at 159 billion dollars. We use stripe, because we want you, our customers to
                know. That your Credit Card information is always safe, and we never see it, nor have access to it.
              </p>
            </div>
          </Section>

          <Section n={2} title="Who we are & acceptance">
            <p>
              "Your Classic Style", "we", "us" and "our" refer to the operator of the Your Classic Style website and
              related services (the "Platform"). "You" refers to any person who accesses or uses the Platform, whether
              as a visitor, buyer, or seller.
            </p>
            <p>
              By accessing or using the Platform you confirm that you have read, understood and agree to these Terms,
              our fee schedule, and any policies referenced here. If you do not agree, you must not use the Platform.
            </p>
          </Section>

          <Section n={3} title="Definitions">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Buyer</strong> — a user who purchases or offers to purchase an Item.</li>
              <li><strong>Seller</strong> — a user who lists an Item for sale.</li>
              <li><strong>Item</strong> — a pre-owned, vintage or new-with-tags product offered through a Listing.</li>
              <li><strong>Listing</strong> — the page created by a Seller describing an Item, its condition, measurements, photos and disclosures.</li>
              <li><strong>Buyer Protection</strong> — the fee-funded service that holds a Seller's payout and backs the authenticity guarantee.</li>
              <li><strong>Order</strong> — a completed purchase formed when a Buyer's payment is confirmed.</li>
            </ul>
          </Section>

          <Section n={4} title="Eligibility & your account">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account and transact.</li>
              <li>You agree to provide accurate, current information and to keep your login credentials secure. You are responsible for all activity under your account.</li>
              <li>You may hold one account unless we authorise otherwise. We may refuse, suspend or close accounts at our discretion, including for policy breaches or fraud.</li>
            </ul>
          </Section>

          <Section n={5} title="Our role in transactions">
            <p>
              The Platform is a venue that connects individual Buyers and Sellers. Unless expressly stated, we are not
              the seller, importer, manufacturer or owner of any Item. The contract of sale for an Item is formed
              directly between the Buyer and the Seller.
            </p>
            <p>
              We provide tools such as listing creation, an optional AI authenticity screening that Sellers may choose
              to run to reassure Buyers of an Item's authenticity, payment processing (via our payment partner), Buyer
              Protection and dispute handling, but we do not guarantee the quality, safety, legality or accuracy of
              Listings beyond the protections expressly described in Section 13.
            </p>
          </Section>

          <Section n={6} title="Listing rules & quality standards">
            <p>To keep the marketplace to a near-excellent standard, every Listing must:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Be graded honestly using our condition scale — <em>New with tags, New without tags, Excellent, Very good</em> or <em>Good</em>. We do not accept Items graded below <em>Good</em>.</li>
              <li>Include at least three clear photos of the actual Item, plus a close-up of the brand / size / care label and a close-up of any flaws (or an explicit "no visible flaws" attestation).</li>
              <li>State exact measurements appropriate to the category (e.g. chest and length for apparel; insole length for shoes).</li>
              <li>Disclose known issues, including odor, smoke or pet exposure, alterations, repairs and missing components.</li>
              <li>Be accurately described, correctly priced, and in the Seller's lawful possession and available to ship.</li>
            </ul>
            <p>Sellers must confirm they have inspected an Item before shipping. Listings that fail these standards may be sent back, hidden or removed.</p>
          </Section>

          <Section n={7} title="Authenticity & anti-counterfeit policy">
            <p>
              Counterfeit goods are strictly prohibited. Every Item must be listed under one of three honest claims:
              (a) <strong>Authentic branded original</strong> — a Seller has the option to prove authenticity through
              our AI authenticity screening and proof close-ups if they want to assure the Buyer of the Item's
              authenticity; (b) <strong>Unbranded / Inspired-by</strong>, with no brand claim; or
              (c) <strong>Disclosed replica</strong>, clearly labelled and price-capped.
            </p>
            <p>
              AI screening is an optional aid, not a definitive certification. Listing a counterfeit as authentic, or
              otherwise misrepresenting a brand, results in removal of the Listing and a strike against the Seller.
              Repeated breaches (three strikes) lead to suspension. We may report suspected criminal counterfeiting to
              rights holders and authorities.
            </p>
          </Section>

          <Section n={8} title="Prohibited, restricted & dangerous goods">
            <p>
              <strong>You are solely responsible for ensuring every Item you list is legal to sell, safe, genuine and
              permitted to ship.</strong> You must comply with all applicable laws and with the rules of the carriers,
              postal services and customs authorities used to fulfil the Order. The following are strictly prohibited on
              the Platform, and you may not list, sell, buy, or ship them:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Counterfeit, replica-passed-as-genuine, stolen, smuggled or otherwise unlawfully obtained goods;</li>
              <li>Items that infringe intellectual-property, trademark, copyright or publicity rights;</li>
              <li>Weapons, firearms, firearm parts, ammunition, explosives, fireworks and self-defence sprays;</li>
              <li>Illegal drugs and paraphernalia, tobacco and vaping products, prescription or unapproved medicines and supplements;</li>
              <li>Alcoholic beverages and any controlled or regulated substance;</li>
              <li>Soiled, contaminated, recalled, unsafe or non-compliant items, or anything failing basic hygiene standards (including used cosmetics, opened personal-care products, and used underwear where restricted);</li>
              <li>Fur, ivory, exotic leathers and products from protected or endangered species where restricted by law;</li>
              <li>Human remains or bodily fluids, live animals, food and perishable goods;</li>
              <li>Anything that is not genuine apparel, footwear or accessories consistent with the Platform's purpose, and anything otherwise prohibited by applicable law.</li>
            </ul>

            <div className="hairline p-4 mt-4" style={{ borderColor: "#E11D48", background: "rgba(225,29,72,0.04)" }} data-testid="terms-dangerous-goods">
              <div className="text-xs uppercase tracking-[0.16em] mb-2" style={{ color: "#E11D48" }}>Hazardous / dangerous goods — do not ship</div>
              <p>
                Many ordinary items are classified as <strong>dangerous goods (hazmat)</strong> and are restricted or
                banned from carrier and postal networks. You must not list or ship these unless they are fully compliant,
                lawfully packaged and declared under the relevant carrier and IATA/DOT/postal rules — and, in most cases,
                you simply may not ship them at all. These include, without limitation:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 mt-2">
                <li><strong>Batteries &amp; electronics</strong> — loose lithium-ion / lithium-metal batteries, power banks, and devices containing them where prohibited or undeclared;</li>
                <li><strong>Alcohol-based liquids</strong> — perfumes, colognes, fragrances and other flammable scented liquids;</li>
                <li><strong>Aerosols &amp; pressurised containers</strong> — hairspray, deodorant, dry shampoo, spray paint, compressed gas;</li>
                <li><strong>Flammable solids &amp; liquids</strong> — nail polish and remover, lighter fluid, solvents, lighters, matches;</li>
                <li><strong>Corrosives, oxidisers, magnets, mercury and any material a carrier classifies as hazardous.</strong></li>
              </ul>
            </div>

            <p className="mt-4">
              Before publishing, you must confirm the Item is legal, safe, genuine and permitted to ship. Breach of this
              Section may result in immediate removal of the Listing, a strike, suspension or termination of your
              account, withholding or reversal of payouts, and referral to rights holders, carriers or authorities.
            </p>
            <p>
              <strong>The Platform does not physically inspect Items and is not responsible or liable for any Item you
              list or its shipment.</strong> To the maximum extent permitted by law, you assume all responsibility and
              risk for your Items — including any injury, death, property damage, fire, contamination, fines, penalties
              or claims arising from a prohibited, unsafe, hazardous or improperly shipped Item — and you indemnify the
              Platform accordingly (see Section 21). Buyers use all Items at their own risk and must follow all safety
              and care instructions.
            </p>
          </Section>

          <Section n={9} title="Buying, offers & orders">
            <p>
              Buyers may add Items to their bag, save Items, and accept private price offers sent by Sellers. Prices are
              shown in US dollars unless stated otherwise. An Order is formed only when payment is confirmed; until then,
              adding an Item to a bag or receiving an offer does not reserve it, and Items are sold on a first-paid basis.
            </p>
          </Section>

          <Section n={10} title="Fees, commission & subscriptions">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Listing</strong> is free.</li>
              <li><strong>Buyer Protection fee</strong> — charged to the Buyer at checkout as a flat 5% of the item price plus a fixed $0.70 per order. It funds the authenticity guarantee, payout hold and claim reviews.</li>
              <li><strong>Seller commission</strong> — we do not charge a per-sale commission; Sellers keep 100% of the item price they set.</li>
              <li><strong>Designer authentication fee</strong> — for higher-value branded Items, deducted from the Seller's payout on sale, never upfront.</li>
              <li><strong>Optional subscriptions</strong> (e.g. Pro, Store) unlock premium tools for a recurring fee, billed in advance and cancellable for future periods.</li>
            </ul>
            <p>Current amounts are shown on the <Link to="/pricing" className="underline">Fees &amp; plans</Link> page and at checkout, and may change under Section 24.</p>
          </Section>

          <Section n={11} title="Payments & processing">
            <p>
              Payments are processed by our third-party payment partner. By transacting you agree to their terms. We do
              not store full card details. You authorise us and our payment partner to charge the applicable Item price,
              fees, shipping and taxes to your chosen payment method.
            </p>
          </Section>

          <Section n={12} title="Shipping, pickup & delivery">
            <p>
              Buyers choose a delivery method at checkout, which may include pickup points/parcel lockers, standard or
              express home delivery, or local pickup where offered. Shipping charges are shown before payment. Sellers
              must dispatch promptly after confirming inspection, use the selected method, and provide tracking where
              available. Risk and title pass to the Buyer on delivery. Delivery estimates are indicative, not guaranteed.
            </p>
          </Section>

          <Section n={13} title="Buyer protection & authenticity guarantee">
            <p>
              Eligible Orders are covered by a limited authenticity guarantee. The guarantee window begins when the Item
              is delivered (or when delivery is confirmed) and lasts for the number of days shown on the Order. During
              this window the Seller's payout is held. If the Item is materially not as described or is found to be
              counterfeit, the Buyer may file a claim (Section 14). Buyer Protection does not cover buyer's remorse,
              minor variations consistent with a disclosed used condition, or issues arising after normal wear.
            </p>
          </Section>

          <Section n={14} title="Returns, claims & disputes">
            <p>
              As a marketplace for individually-owned used goods, Sellers are not obliged to accept change-of-mind
              returns unless they offer them. However, if an Item is significantly not as described, counterfeit, or does
              not arrive, the Buyer may open a claim from the Purchases page within the guarantee window with supporting
              photos. Our team and/or AI review the evidence against the Listing and the Seller's proof. Sellers are given
              an opportunity to respond. Where a claim is upheld, the Buyer is refunded and the payout is reversed; where
              inconclusive, we generally resolve in the Buyer's favour without penalising the Seller. Our decision on a
              claim is final for the purposes of Buyer Protection, without prejudice to your statutory rights. See our{" "}
              <Link to="/refunds" className="underline" data-testid="terms-refunds-link">Refund &amp; Returns Policy</Link>{" "}
              for full details.
            </p>
          </Section>

          <Section n={15} title="Seller payouts & holds">
            <p>
              A Seller's proceeds (Item price less commission, authentication and any applicable fees) are held during the
              guarantee window and released afterwards, provided no claim is pending. If a claim is upheld or an Order is
              refunded, the corresponding amount is reversed or withheld. We may delay or withhold payouts where we
              reasonably suspect fraud, chargebacks or policy breaches.
            </p>
          </Section>

          <Section n={16} title="Prohibited conduct">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Circumventing the Platform to transact off-site to avoid fees or protections;</li>
              <li>Fraud, deceptive listings, fake reviews, shill bidding or price manipulation;</li>
              <li>Harassment, hate speech, or sharing others' personal data without consent;</li>
              <li>Scraping, reverse-engineering, or interfering with the Platform's security or operation.</li>
            </ul>
          </Section>

          <Section n={17} title="Your content & our licence">
            <p>
              You retain ownership of the photos and text you upload, but you grant us a worldwide, non-exclusive,
              royalty-free licence to host, display, resize and promote that content in connection with operating and
              marketing the Platform. You warrant that you own or have the rights to all content you upload and that it
              does not infringe third-party rights.
            </p>
          </Section>

          <Section n={18} title="Privacy & data">
            <p>
              <strong>We never collect, sell, nor store your data on our site beyond that which is needed to make this a
              functional site.</strong>
            </p>
            <p>
              We process personal data in accordance with our{" "}
              <Link to="/privacy" className="underline" data-testid="terms-privacy-link">Privacy Policy</Link>. By using
              the Platform you consent to that processing, including sharing the limited data necessary to complete a
              transaction (for example, delivery details shared between Buyer and Seller or with carriers).
            </p>
          </Section>

          <Section n={19} title="Disclaimers">
            <p>
              The Platform is provided "as is" and "as available". To the fullest extent permitted by law, we disclaim
              all warranties, express or implied, including merchantability, fitness for a particular purpose and
              non-infringement. We do not warrant that the Platform will be uninterrupted, secure or error-free, nor that
              AI screening will detect every counterfeit.
            </p>
          </Section>

          <Section n={20} title="Limitation of liability">
            <p>
              To the fullest extent permitted by law, we are not liable for indirect, incidental, special, consequential
              or punitive damages, or loss of profits, data or goodwill. Our total aggregate liability arising out of or
              relating to the Platform or a transaction will not exceed the greater of the fees we received from you for
              the relevant Order or USD 100. Nothing in these Terms excludes liability that cannot be excluded by law,
              including your non-waivable consumer rights.
            </p>
          </Section>

          <Section n={21} title="Indemnification">
            <p>
              You agree to indemnify, defend and hold harmless Your Classic Style and its operators, officers and agents
              from and against any and all claims, liabilities, damages, losses, fines, penalties and expenses (including
              reasonable legal fees) arising out of or related to: your Listings and your Items; any Item that is
              unlawful, unsafe, hazardous, recalled, counterfeit, or a restricted/dangerous good; any personal injury,
              death or property damage caused by your Item; any breach of shipping, customs, dangerous-goods or other
              regulations; your use of the Platform; or your breach of these Terms or any applicable law. This obligation
              survives termination of your account.
            </p>
          </Section>

          <Section n={22} title="Suspension & termination">
            <p>
              You may close your account at any time. We may suspend or terminate access, remove Listings, or withhold
              payouts where you breach these Terms, where required by law, or to protect users and the Platform. Sections
              that by their nature should survive termination (including fees owed, licences, disclaimers, liability and
              dispute resolution) will survive.
            </p>
          </Section>

          <Section n={23} title="Governing law & dispute resolution">
            <p>
              These Terms are governed by the laws of the State of Georgia and the United States, without regard to
              conflict-of-law rules. Subject to any mandatory consumer-protection rights you may have, you agree that
              disputes will be resolved exclusively in the state or federal courts located in the State of Georgia, USA,
              and you consent to their jurisdiction. <em>(Your lawyer may add or adjust venue and any arbitration/class-action
              waiver terms to suit your business.)</em>
            </p>
          </Section>

          <Section n={24} title="Changes to these terms">
            <p>
              We may update these Terms from time to time. Material changes will be posted here with a new "last updated"
              date and, where appropriate, notified to you. Your continued use of the Platform after changes take effect
              constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section n={25} title="Contact us">
            <p>
              Questions about these Terms? Contact us at{" "}
              <span className="underline" data-testid="terms-support-email">kishawhite@yourclassicstyle.onmicrosoft.com</span>.
            </p>
          </Section>

          <p className="mt-12 text-xs text-neutral-400">
            © {new Date().getFullYear()} Your Classic Style. These Terms work alongside our{" "}
            <Link to="/pricing" className="underline">Fees &amp; plans</Link> and{" "}
            <Link to="/proof-guide" className="underline">Authenticity proof guide</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
